import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import useBreakpoint from '../hooks/useBreakpoint';

const T = { ink: '#111', muted: '#888', border: '#e5e5e5', blue: '#2563eb' };

const inputStyle = {
  width: '100%', border: `1px solid ${T.border}`,
  padding: '10px 12px', fontSize: 13, boxSizing: 'border-box',
  color: T.ink, background: 'white', outline: 'none', borderRadius: 8,
};

const UNIT_TYPES = [
  { value: 'wohnung',   label: 'Wohnung' },
  { value: 'gewerbe',   label: 'Gewerbeeinheit' },
  { value: 'haus',      label: 'Haus' },
  { value: 'sonstiges', label: 'Sonstiges' },
];

const EMPTY_FORM = {
  property_id: '', unit_number: '', type: 'wohnung',
  floor: '', size_sqm: '', rooms: '', cold_rent: '', warm_rent: '', notes: '',
};

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: T.muted, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.4px' }}>{label}</label>
      {children}
    </div>
  );
}

export default function Units() {
  const { user } = useAuth();
  const { addToast } = useToast();
  const { isMobile } = useBreakpoint();

  const [units, setUnits]           = useState([]);
  const [properties, setProperties] = useState([]);
  const [tenants, setTenants]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [showForm, setShowForm]     = useState(false);
  const [editing, setEditing]       = useState(null);
  const [form, setForm]             = useState(EMPTY_FORM);
  const [saving, setSaving]         = useState(false);
  const [filterProp, setFilterProp] = useState('');

  useEffect(() => { fetchAll(); }, []);

  async function fetchAll() {
    const [{ data: u }, { data: p }, { data: t }] = await Promise.all([
      supabase.from('units').select('*').eq('owner_id', user.id).order('unit_number'),
      supabase.from('properties').select('id, name').eq('owner_id', user.id).order('name'),
      supabase.from('tenants').select('id, first_name, last_name, unit_id').eq('owner_id', user.id),
    ]);
    setUnits(u || []);
    setProperties(p || []);
    setTenants(t || []);
    setLoading(false);
  }

  function propName(id) {
    return properties.find(p => p.id === id)?.name || '–';
  }

  function tenantForUnit(unitId) {
    return tenants.find(t => t.unit_id === unitId) || null;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.property_id) { addToast('Bitte ein Objekt wählen.', 'error'); return; }
    if (!form.unit_number.trim()) { addToast('Einheitenbezeichnung erforderlich.', 'error'); return; }
    setSaving(true);

    const payload = {
      property_id: form.property_id,
      unit_number: form.unit_number.trim(),
      type:        form.type || 'wohnung',
      floor:       form.floor !== '' ? Number(form.floor) : null,
      size_sqm:    form.size_sqm !== '' ? Number(form.size_sqm) : null,
      rooms:       form.rooms !== '' ? Number(form.rooms) : null,
      cold_rent:   form.cold_rent !== '' ? Number(form.cold_rent) : 0,
      warm_rent:   form.warm_rent !== '' ? Number(form.warm_rent) : null,
      notes:       form.notes || null,
      owner_id:    user.id,
    };

    const { error } = editing
      ? await supabase.from('units').update(payload).eq('id', editing).eq('owner_id', user.id)
      : await supabase.from('units').insert(payload);

    if (error) { addToast('Fehler: ' + error.message, 'error'); setSaving(false); return; }
    await fetchAll();
    setShowForm(false); setEditing(null); setForm(EMPTY_FORM); setSaving(false);
    addToast(editing ? 'Einheit aktualisiert.' : 'Einheit angelegt.', 'success');
  }

  async function handleDelete(id) {
    const tenant = tenantForUnit(id);
    if (tenant) {
      addToast(`Einheit hat noch Mieter (${tenant.first_name} ${tenant.last_name}). Zuerst Mieter entfernen.`, 'error');
      return;
    }
    if (!window.confirm('Einheit wirklich löschen?')) return;
    await supabase.from('units').delete().eq('id', id).eq('owner_id', user.id);
    setUnits(u => u.filter(x => x.id !== id));
    addToast('Einheit gelöscht.', 'success');
  }

  function openEdit(u) {
    setEditing(u.id);
    setForm({
      property_id: u.property_id || '',
      unit_number: u.unit_number || '',
      type:        u.type || 'wohnung',
      floor:       u.floor ?? '',
      size_sqm:    u.size_sqm ?? '',
      rooms:       u.rooms ?? '',
      cold_rent:   u.cold_rent ?? '',
      warm_rent:   u.warm_rent ?? '',
      notes:       u.notes || '',
    });
    setShowForm(true);
  }

  const filtered = filterProp
    ? units.filter(u => u.property_id === filterProp)
    : units;

  const occupied = units.filter(u => u.is_occupied || tenantForUnit(u.id)).length;

  return (
    <div style={{ maxWidth: 1100, color: T.ink }}>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: isMobile ? 22 : 28, fontWeight: 800, letterSpacing: '-0.6px' }}>Einheiten</h1>
          <div style={{ fontSize: 13, color: T.muted, marginTop: 4 }}>
            {units.length} Einheiten · {occupied} belegt · {units.length - occupied} leer
          </div>
        </div>
        <button
          onClick={() => { setEditing(null); setForm(EMPTY_FORM); setShowForm(true); }}
          className="btn-primary" style={{ fontSize: 13, height: 38 }}
        >
          + Einheit hinzufügen
        </button>
      </div>

      {/* Filter */}
      {properties.length > 1 && (
        <div style={{ marginBottom: 20 }}>
          <select
            value={filterProp}
            onChange={e => setFilterProp(e.target.value)}
            style={{ ...inputStyle, width: 'auto', minWidth: 200, height: 38 }}
          >
            <option value="">Alle Objekte</option>
            {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div style={{ background: 'white', border: `1px solid ${T.border}`, borderRadius: 16, padding: 32, textAlign: 'center', color: T.muted }}>Lädt…</div>
      ) : filtered.length === 0 ? (
        <div style={{ background: 'white', border: `1.5px solid ${T.border}`, borderRadius: 16, padding: '48px 32px', textAlign: 'center' }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: T.ink, marginBottom: 8 }}>Noch keine Einheiten</div>
          <div style={{ fontSize: 13, color: T.muted, marginBottom: 20 }}>
            Lege Einheiten (Wohnungen, Gewerbe) innerhalb deiner Objekte an.
          </div>
          {properties.length === 0 ? (
            <div style={{ fontSize: 13, color: '#dc2626' }}>Zuerst ein Objekt unter "Objekte" anlegen.</div>
          ) : (
            <button onClick={() => { setEditing(null); setForm(EMPTY_FORM); setShowForm(true); }} className="btn-primary">
              + Erste Einheit hinzufügen
            </button>
          )}
        </div>
      ) : (
        <div style={{ background: 'white', border: `1px solid ${T.border}`, borderRadius: 16, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
          {/* Table header */}
          {!isMobile && (
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1.2fr 0.8fr 0.8fr 0.8fr 1fr auto', padding: '10px 20px', borderBottom: `1px solid ${T.border}`, background: '#fafafa' }}>
              {['Einheit', 'Objekt', 'Typ', 'Fläche', 'Zimmer', 'Mieter', ''].map(h => (
                <span key={h} style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase', color: '#9ca3af' }}>{h}</span>
              ))}
            </div>
          )}
          {filtered.map((u, i) => {
            const tenant  = tenantForUnit(u.id);
            const isLast  = i === filtered.length - 1;
            const typeLabel = UNIT_TYPES.find(t => t.value === u.type)?.label || u.type;

            if (isMobile) {
              return (
                <div key={u.id} style={{ padding: '16px 20px', borderBottom: isLast ? 'none' : `1px solid ${T.border}` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <div style={{ fontSize: 15, fontWeight: 700 }}>{u.unit_number}</div>
                      <div style={{ fontSize: 12, color: T.muted, marginTop: 2 }}>{propName(u.property_id)} · {typeLabel}</div>
                      {u.size_sqm && <div style={{ fontSize: 12, color: T.muted }}>{u.size_sqm} m²{u.rooms ? ` · ${u.rooms} Zi.` : ''}</div>}
                      {tenant && <div style={{ fontSize: 12, color: T.blue, marginTop: 4, fontWeight: 600 }}>{tenant.first_name} {tenant.last_name}</div>}
                      {!tenant && <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>Leer</div>}
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button onClick={() => openEdit(u)} className="btn-ghost" style={{ fontSize: 12, height: 32, padding: '0 12px' }}>Bearb.</button>
                      <button onClick={() => handleDelete(u.id)} className="btn-ghost" style={{ fontSize: 12, height: 32, padding: '0 12px', color: '#dc2626' }}>Löschen</button>
                    </div>
                  </div>
                </div>
              );
            }

            return (
              <div key={u.id} style={{
                display: 'grid', gridTemplateColumns: '2fr 1.2fr 0.8fr 0.8fr 0.8fr 1fr auto',
                padding: '14px 20px', alignItems: 'center',
                borderBottom: isLast ? 'none' : `1px solid ${T.border}`,
              }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700 }}>{u.unit_number}</div>
                  {u.floor != null && <div style={{ fontSize: 12, color: T.muted }}>{u.floor}. Obergeschoss</div>}
                </div>
                <div style={{ fontSize: 13, color: T.muted }}>{propName(u.property_id)}</div>
                <div style={{ fontSize: 13 }}>{typeLabel}</div>
                <div style={{ fontSize: 13, color: T.muted }}>{u.size_sqm ? `${u.size_sqm} m²` : '–'}</div>
                <div style={{ fontSize: 13, color: T.muted }}>{u.rooms ? `${u.rooms} Zi.` : '–'}</div>
                <div>
                  {tenant ? (
                    <span style={{ fontSize: 12, fontWeight: 600, color: T.blue }}>
                      {tenant.first_name} {tenant.last_name}
                    </span>
                  ) : (
                    <span style={{ fontSize: 12, color: '#9ca3af', background: '#f5f5f5', borderRadius: 100, padding: '2px 10px' }}>Leer</span>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => openEdit(u)} className="btn-ghost" style={{ fontSize: 12, height: 32, padding: '0 14px' }}>Bearb.</button>
                  <button onClick={() => handleDelete(u.id)} style={{ fontSize: 12, height: 32, padding: '0 14px', background: 'none', border: `1px solid ${T.border}`, borderRadius: 8, color: '#dc2626', cursor: 'pointer' }}>Löschen</button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal */}
      {showForm && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: isMobile ? 0 : 24 }}
          onClick={e => { if (e.target === e.currentTarget) setShowForm(false); }}
        >
          <div className="immo-modal" style={{ background: 'white', width: '100%', maxWidth: 520, maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ padding: '24px 28px 18px', borderBottom: `1px solid ${T.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>
                  {editing ? 'Bearbeiten' : 'Neue Einheit'}
                </div>
                <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, letterSpacing: '-0.4px' }}>
                  {editing ? 'Einheit bearbeiten' : 'Einheit anlegen'}
                </h2>
              </div>
              <button onClick={() => setShowForm(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 22, color: T.muted, lineHeight: 1, padding: 4 }}>×</button>
            </div>

            <form onSubmit={handleSubmit} style={{ padding: '22px 28px 28px' }}>
              <Field label="Objekt *">
                <select
                  value={form.property_id}
                  onChange={e => setForm({ ...form, property_id: e.target.value })}
                  style={{ ...inputStyle, cursor: 'pointer' }}
                  required
                >
                  <option value="">– Objekt wählen –</option>
                  {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </Field>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <Field label="Bezeichnung *">
                  <input
                    type="text" required
                    value={form.unit_number}
                    onChange={e => setForm({ ...form, unit_number: e.target.value })}
                    placeholder="z.B. Whg. 3, EG links"
                    style={inputStyle}
                  />
                </Field>
                <Field label="Typ">
                  <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })} style={{ ...inputStyle, cursor: 'pointer' }}>
                    {UNIT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </Field>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14 }}>
                <Field label="Stockwerk">
                  <input type="number" value={form.floor} onChange={e => setForm({ ...form, floor: e.target.value })} placeholder="z.B. 1" style={inputStyle} />
                </Field>
                <Field label="Fläche (m²)">
                  <input type="number" min="0" step="0.01" value={form.size_sqm} onChange={e => setForm({ ...form, size_sqm: e.target.value })} style={inputStyle} />
                </Field>
                <Field label="Zimmer">
                  <input type="number" min="0" step="0.5" value={form.rooms} onChange={e => setForm({ ...form, rooms: e.target.value })} style={inputStyle} />
                </Field>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <Field label="Kaltmiete (€)">
                  <input type="number" min="0" step="0.01" value={form.cold_rent} onChange={e => setForm({ ...form, cold_rent: e.target.value })} style={inputStyle} />
                </Field>
                <Field label="Warmmiete (€)">
                  <input type="number" min="0" step="0.01" value={form.warm_rent} onChange={e => setForm({ ...form, warm_rent: e.target.value })} style={inputStyle} />
                </Field>
              </div>

              <Field label="Notizen">
                <textarea
                  value={form.notes}
                  onChange={e => setForm({ ...form, notes: e.target.value })}
                  rows={3}
                  style={{ ...inputStyle, resize: 'vertical' }}
                />
              </Field>

              <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
                <button type="submit" disabled={saving} className="btn-primary" style={{ flex: 1, height: 44, fontSize: 14, opacity: saving ? 0.6 : 1 }}>
                  {saving ? 'Speichert…' : 'Speichern'}
                </button>
                <button type="button" onClick={() => setShowForm(false)} className="btn-secondary" style={{ height: 44, fontSize: 14, padding: '0 20px' }}>
                  Abbrechen
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
